import {
  Button,
  Box,
  Stack,
  Typography,
  Chip,
  Divider,
} from "@mui/material";
import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TimelineIcon from "@mui/icons-material/Timeline";
import EditNoteIcon from "@mui/icons-material/EditNote";
import {
  describirMovimientoEtapa,
  historialEdicionesPorLead,
  historialEtapasPorLead,
} from "./leadHistorialUtils";

function formatFecha(fechaHora) {
  if (!fechaHora) return "—";
  try {
    return new Date(fechaHora).toLocaleString(undefined, {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return fechaHora;
  }
}

export default function LeadHistorialPanel({
  leadId,
  config,
  historialEtapas = [],
  historialEdicionesLead = [],
  historialAsignaciones = [],
  compact = false,
}) {
  const [showAll, setShowAll] = useState(false);
  const limit = compact ? 8 : 20;
  const etapas = historialEtapasPorLead(historialEtapas, leadId);
  const ediciones = historialEdicionesPorLead(historialEdicionesLead, leadId);

  const asignaciones = historialAsignaciones.filter(h => h.leadId === leadId).sort((a, b) => String(b.fechaHora).localeCompare(String(a.fechaHora)));
  if (etapas.length === 0 && ediciones.length === 0 && asignaciones.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        Sin movimientos de etapa ni ediciones registradas aún.
      </Typography>
    );
  }

  return (
    <Stack spacing={compact ? 1.5 : 2}>
      <Box>
        <Typography variant="subtitle2" fontWeight={700}>Asignaciones ({asignaciones.length})</Typography>
        {asignaciones.slice(0, showAll ? undefined : limit).map(h => <Box key={h.id} mt={1}>
          <Typography variant="caption" color="text.secondary">{formatFecha(h.fechaHora)} · {h.actorNombre}</Typography>
          <Typography variant="body2">{h.vendedorAnteriorNombre || 'Sin asignar'} → {h.vendedorNuevoNombre || 'Sin asignar'}</Typography>
        </Box>)}
      </Box>
      <Box>
        <Stack direction="row" spacing={0.75} alignItems="center" mb={1}>
          <TimelineIcon sx={{ fontSize: 18, color: "primary.main" }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Movimiento entre etapas ({etapas.length})
          </Typography>
        </Stack>
        {etapas.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            Sin cambios de etapa registrados.
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            {etapas.slice(0, showAll ? undefined : limit).map((entrada) => (
              <Box key={entrada.id}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {formatFecha(entrada.fechaHora)}
                  {entrada.actorNombre && ` · ${entrada.actorNombre} · ${entrada.vendedorNombre || 'Sin asignar'}`}
                </Typography>
                <Typography variant="body2">{describirMovimientoEtapa(config, entrada)}</Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      <Divider flexItem />

      <Box>
        <Stack direction="row" spacing={0.75} alignItems="center" mb={1}>
          <EditNoteIcon sx={{ fontSize: 18, color: "secondary.main" }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Historial de ediciones ({ediciones.length})
          </Typography>
        </Stack>
        {ediciones.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            Sin ediciones de datos registradas.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {ediciones.slice(0, showAll ? undefined : limit).map((entrada) => (
              <Box key={entrada.id}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  {formatFecha(entrada.fechaHora)}
                  {entrada.actorNombre && ` · ${entrada.actorNombre}`}
                </Typography>
                <Stack spacing={0.5}>
                  {entrada.cambios.map((c) => (
                    <Box key={`${entrada.id}-${c.campo}`}>
                      <Typography variant="caption" fontWeight={600}>
                        {c.etiqueta}
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                        <Chip label={c.valorAnterior} size="small" variant="outlined" sx={{ maxWidth: "100%", height: "auto", "& .MuiChip-label": { whiteSpace: "normal" } }} />
                        <Typography variant="caption" color="text.secondary">→</Typography>
                        <Chip label={c.valorNuevo} size="small" color="primary" variant="outlined" sx={{ maxWidth: "100%", height: "auto", "& .MuiChip-label": { whiteSpace: "normal" } }} />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
      {Math.max(asignaciones.length, etapas.length, ediciones.length) > limit && <Button size="small" startIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />} onClick={() => setShowAll(v => !v)}>{showAll ? 'Ver menos' : 'Ver historial completo'}</Button>}
    </Stack>
  );
}
